import { createContext, useContext } from 'react';
import { Team, Membership, Invitation, OnboardingStatusEnum } from 'api-client';

const STORAGE_KEYS = {
  TEAM_ID: 'flawtrack_team_id',
  TEAM_SLUG: 'flawtrack_team_slug',
  ONBOARDING_STATUS: 'flawtrack_team_onboarding_status'
} as const;

export interface TeamsContextType {
  // Teams state
  teams: Team[];
  currentTeam: Team | null;
  isLoading: boolean;
  error: string | null;

  // Team management
  switchTeam: (team: Team) => void;
  initializeTeams: () => Promise<void>;
  createTeam: (name: string) => Promise<Team>;
  updateTeam: (team: Team) => Promise<Team>;
  deleteTeam: (teamId: number) => Promise<void>;
  refetchCurrentTeam: () => Promise<Team | null>;
  
  // Team members
  addMember: (teamSlug: string, email: string, role: string) => Promise<Membership>;
  removeMember: (teamSlug: string, memberId: string) => Promise<void>;
  updateMember: (teamSlug: string, memberId: string, role: string) => Promise<Membership>;
  
  // Team invitations
  createInvitation: (teamSlug: string, email: string, role: string) => Promise<Invitation>;
  cancelInvitation: (teamSlug: string, invitationId: string) => Promise<void>;
}

// Local storage functions
export const saveTeamToStorage = (team: Team) => {
  localStorage.setItem(STORAGE_KEYS.TEAM_ID, team.id.toString());
  if (team.slug) {
    localStorage.setItem(STORAGE_KEYS.TEAM_SLUG, team.slug);
  }
  if (team.onboardingStatus) {
    localStorage.setItem(STORAGE_KEYS.ONBOARDING_STATUS, team.onboardingStatus);
  }
};

export const loadTeamFromStorage = () => {
  return {
    teamId: localStorage.getItem(STORAGE_KEYS.TEAM_ID),
    teamSlug: localStorage.getItem(STORAGE_KEYS.TEAM_SLUG),
    onboardingStatus: localStorage.getItem(STORAGE_KEYS.ONBOARDING_STATUS) as OnboardingStatusEnum | null
  };
};

export const clearTeamFromStorage = () => {
  localStorage.removeItem(STORAGE_KEYS.TEAM_ID);
  localStorage.removeItem(STORAGE_KEYS.TEAM_SLUG);
  localStorage.removeItem(STORAGE_KEYS.ONBOARDING_STATUS);
};

// Create context with default values
export const TeamsContext = createContext<TeamsContextType>({
  teams: [],
  currentTeam: null,
  isLoading: false,
  error: null,
  switchTeam: () => {},
  initializeTeams: async () => {},
  createTeam: async (name: string) => Promise.resolve({} as Team),
  updateTeam: async (team: Team) => Promise.resolve(team),
  deleteTeam: async (teamId: number) => Promise.resolve(),
  refetchCurrentTeam: async () => Promise.resolve(null),
  addMember: async (teamSlug: string, email: string, role: string) => Promise.resolve({} as Membership),
  removeMember: async (teamSlug: string, memberId: string) => Promise.resolve(),
  updateMember: async (teamSlug: string, memberId: string, role: string) => Promise.resolve({} as Membership),
  createInvitation: async (teamSlug: string, email: string, role: string) => Promise.resolve({} as Invitation),
  cancelInvitation: async (teamSlug: string, invitationId: string) => Promise.resolve()
});

// Custom hook for using teams context
export const useTeams = () => {
  const context = useContext(TeamsContext);
  if (!context) {
    throw new Error('useTeams must be used within a TeamsProvider');
  }
  return context;
};